@if (open()) {
  <div class="abl-overlay" (click)="close()">
    <div class="abl-modal" role="dialog" aria-modal="true" aria-labelledby="abl-title"
         tabindex="-1" (click)="stop($event)">

      <!-- ── Header ── -->
      <header class="abl-head">
        <span class="abl-head__icon"><i class="pi pi-compass"></i></span>
        <div class="abl-head__titles">
          <h2 class="abl-head__title" id="abl-title">Abilities</h2>
          <p class="abl-head__sub">{{ total() }} abilities across {{ systems() }} systems — agents use them with your access</p>
        </div>
        <label class="abl-search">
          <i class="pi pi-search abl-search__icon"></i>
          <input
            class="abl-search__input"
            type="text"
            placeholder="Search abilities…"
            aria-label="Search abilities"
            [value]="query()"
            (input)="onSearchInput($event)"
          />
          @if (query()) {
            <button type="button" class="abl-search__clear" (click)="clearSearch()"
                    aria-label="Clear search"><i class="pi pi-times"></i></button>
          }
        </label>
        <button type="button" class="abl-link" (click)="goToCatalog()">
          Full catalog <i class="pi pi-arrow-right"></i>
        </button>
        <button class="abl-close" type="button" (click)="close()" aria-label="Close abilities">
          <i class="pi pi-times"></i>
        </button>
      </header>

      <!-- ── Body ── -->
      @if (loading()) {
        <div class="abl-state"><i class="pi pi-spinner pi-spin"></i> Loading abilities…</div>
      } @else if (error()) {
        <div class="abl-state abl-state--error" role="alert">
          <i class="pi pi-exclamation-circle"></i>
          <span>Couldn't load abilities.</span>
          <button type="button" class="abl-btn abl-btn--ghost" (click)="retry()">
            <i class="pi pi-refresh"></i> Retry
          </button>
        </div>
      } @else if (empty()) {
        <div class="abl-state">
          <i class="pi pi-compass"></i>
          <span>No abilities available yet.</span>
        </div>
      } @else {
        <div class="abl-body">

          <nav class="abl-side" aria-label="Ability categories">
            <button type="button" class="abl-side__item"
                    [class.abl-side__item--on]="!searching() && view() === 'browse' && activeCategory() === null"
                    (click)="selectCategory(null)">
              <i class="pi pi-th-large abl-side__glyph"></i>
              <span class="abl-side__name">All</span>
              <span class="abl-side__n">{{ total() }}</span>
            </button>
            @for (c of categories(); track c.category) {
              <button type="button" class="abl-side__item"
                      [class.abl-side__item--on]="!searching() && view() === 'browse' && activeCategory() === c.category"
                      (click)="selectCategory(c.category)">
                <i class="pi {{ c.icon }} abl-side__glyph"></i>
                <span class="abl-side__name">{{ c.category }}</span>
                <span class="abl-side__n">{{ c.count }}</span>
              </button>
            }
            @if (comingSoon().length > 0) {
              <div class="abl-side__rule"></div>
              <button type="button" class="abl-side__item abl-side__item--soon"
                      [class.abl-side__item--on]="!searching() && view() === 'soon'"
                      (click)="selectSoon()">
                <i class="pi pi-clock abl-side__glyph"></i>
                <span class="abl-side__name">Coming soon</span>
                <span class="abl-side__n">{{ comingSoon().length }}</span>
              </button>
            }
          </nav>

          <section class="abl-main">

            @if (searching()) {
              <!-- Search view: flat hits across every category. -->
              <div class="abl-main__head">
                <h3 class="abl-main__title">
                  {{ hits().length }} {{ hits().length === 1 ? 'result' : 'results' }}
                  for "{{ query().trim() }}"
                </h3>
                <p class="abl-main__desc">Across every category — pick what you can use, or request access.</p>
              </div>
              @if (hits().length === 0) {
                <div class="abl-nomatch">
                  <p>Nothing matched. It may not be on the platform yet —</p>
                  @if (isSearchRequested(query())) {
                    <span class="abl-nomatch__done"><i class="pi pi-check"></i> Requested</span>
                  } @else {
                    <button type="button" class="abl-btn abl-btn--outline" (click)="requestSearch()">
                      Request "{{ query().trim() }}"
                    </button>
                  }
                </div>
              } @else {
                <div class="abl-rows">
                  @for (h of hits(); track h.ability.id) {
                    <ng-container
                      [ngTemplateOutlet]="abilityRow"
                      [ngTemplateOutletContext]="{ $implicit: h.ability, category: h.category }" />
                  }
                </div>
              }
            } @else if (view() === 'soon') {
              <!-- Roadmap view: the admin-managed coming-soon entries. -->
              <div class="abl-main__head">
                <h3 class="abl-main__title">Coming soon</h3>
                <p class="abl-main__desc">What the team is working on next. Not available to build with yet.</p>
              </div>
              <div class="abl-rows">
                @for (c of comingSoon(); track c.id) {
                  <div class="abl-row abl-row--soon">
                    <div class="abl-row__main abl-row__main--static">
                      <span class="abl-tile abl-tile--dim"><i class="pi {{ iconFor(c.icon) }}"></i></span>
                      <span class="abl-row__text">
                        <span class="abl-row__name">{{ c.name }}</span>
                        @if (c.desc) { <span class="abl-row__desc">{{ c.desc }}</span> }
                      </span>
                      <span class="abl-row__meta">{{ c.system }}</span>
                      <span class="abl-pill abl-pill--soon">
                        <i class="pi pi-clock"></i>{{ c.eta || c.badge || 'Planned' }}
                      </span>
                    </div>
                  </div>
                }
              </div>
            } @else {
              <!-- Browse view: one category (or everything). -->
              <div class="abl-main__head">
                <h3 class="abl-main__title">{{ browseTitle() }}
                  <span class="abl-main__count">{{ rows().length }}</span>
                </h3>
                <p class="abl-main__desc">Click an ability you have to pick it for Studio; request the ones you don't.</p>
              </div>
              <div class="abl-rows">
                @for (a of rows(); track a.id) {
                  <ng-container
                    [ngTemplateOutlet]="abilityRow"
                    [ngTemplateOutletContext]="{ $implicit: a, category: null }" />
                }
              </div>
            }

          </section>
        </div>
      }

      <!-- ── Footer ── swaps by context: read-only note ⇄ selection + Build ── -->
      <footer class="abl-foot">
        @if (selectedCount() > 0) {
          <span class="abl-foot__sel">
            {{ selectedCount() }} {{ selectedCount() === 1 ? 'ability' : 'abilities' }} selected
            <span class="abl-foot__sel-hint">— pre-equipped when Studio opens.</span>
          </span>
          <button type="button" class="abl-btn abl-btn--ghost" (click)="clearSelection()">Clear</button>
          <button type="button" class="abl-btn abl-btn--primary" (click)="buildInStudio()">
            Build with {{ selectedCount() }} in Studio <i class="pi pi-arrow-up-right"></i>
          </button>
        } @else {
          <p class="abl-foot__note">
            <i class="pi pi-lock"></i>
            <span>Read-only catalog — browsing grants nothing. Building happens in Studio,
              under the same access rules you see here.</span>
          </p>
        }
      </footer>

    </div>
  </div>

  <!-- Row template — shared by browse and search so the two views can't drift.
       Two sibling buttons, never nested: the row body picks the ability for
       Studio (access-gated), the trailing action requests access. -->
  <ng-template #abilityRow let-a let-category="category">
    <div class="abl-row">
      <button type="button" class="abl-row__main"
              [class.abl-row__main--static]="!a.hasAccess"
              [class.abl-row__main--picked]="isSelected(a.id)"
              [attr.aria-pressed]="a.hasAccess ? isSelected(a.id) : null"
              [attr.aria-label]="a.name + (a.hasAccess ? (isSelected(a.id) ? ' — picked for Studio' : ' — pick for Studio') : ' — access required')"
              (click)="toggleSelect(a)">
        <span class="abl-tile"><i class="pi {{ iconFor(a.icon) }}"></i></span>
        <span class="abl-row__text">
          <span class="abl-row__name">
            {{ a.name }}
            @if (a.write) {
              <span class="abl-flag" title="This ability changes data in {{ a.system }}">
                <i class="pi pi-exclamation-triangle"></i> Takes action
              </span>
            }
          </span>
          @if (a.description) { <span class="abl-row__desc">{{ a.description }}</span> }
        </span>
        <span class="abl-row__meta">
          @if (category) { <span class="abl-row__cat">{{ category }}</span> }
          {{ badgeFor(a) }}
        </span>
        @if (a.hasAccess) {
          <span class="abl-pick" [class.abl-pick--on]="isSelected(a.id)">
            <i class="pi" [class.pi-check]="isSelected(a.id)" [class.pi-plus]="!isSelected(a.id)"></i>
          </span>
        }
      </button>
      @if (!a.hasAccess) {
        @if (isRequested(a.id)) {
          <span class="abl-pill abl-pill--done"><i class="pi pi-check"></i> Requested</span>
        } @else {
          <button type="button" class="abl-btn abl-btn--outline abl-row__cta" (click)="request(a)">
            <i class="pi pi-lock"></i> Request
          </button>
        }
      }
    </div>
  </ng-template>

  <!-- Request dialogs (siblings of the overlay so their clicks never reach the
       modal's close): per-row "Request access" to an existing gated ability,
       or the no-match "Request an ability" flow. -->
  @if (requestTarget(); as ra) {
    <app-ability-request-dialog
      [abilityId]="ra.id" [abilityName]="ra.name" [system]="ra.system"
      source="hub-abilities-modal"
      (sent)="onRequestSent()" (close)="closeRequestDialog()" />
  } @else if (requestTerm(); as term) {
    <app-request-ability-dialog
      [term]="term" source="hub-abilities-modal"
      (submitted)="onSearchRequestSubmitted($event)" (close)="closeRequestDialog()" />
  }
}
